import { fetchAuthSession } from "aws-amplify/auth";

import { config } from "../config";

/** Una fila de la tabla de comparación de la pantalla de revisión. */
export interface FilaComparacion {
  id: string;
  label: string;
  esperado: unknown;
  leido: unknown;
  coincide: boolean;
  /** No había con qué comparar. No es un desacuerdo: no se pinta en rojo. */
  sin_dato: boolean;
}

/** El abono según los movimientos bancarios de ARCA. `null` cuando el depósito
 *  no pasó por el certifier: mejor no mostrar nada que mostrar un dato que
 *  nunca se consultó. */
export interface Movimiento {
  nivel: string;
  banco: string | null;
  /** Fecha del abono según el banco. Es contra esto que se compara el voucher. */
  fecha: string | null;
  /** N° de operación **del banco**. Cuando el match fue por sufijo no es el
   *  mismo que el del voucher, y es este el que sirve para buscar el abono. */
  codigo_movimiento: string | null;
  codigo_voucher: string | null;
  concepto: string | null;
  movement_id: string | null;
  metodo_codigo: string | null;
}

/** Un evento del historial de un depósito, o del log del sistema.
 *  Sale de CloudWatch, donde ya vive con el `execution_id` adentro. */
export interface EventoTraza {
  ts: string;
  componente: string;
  nivel: string;
  mensaje: string;
  execution_id: string;
  etapa: string;
  email: string;
  /** IA · persona · sistema. Cuando un cliente reclama, lo primero que hay
   *  que poder decir es si decidió un modelo o alguien del equipo. */
  actor: string;
  detalle: Record<string, unknown>;
}

/** Quién soy y qué puedo ver. El portal no sabía el rol de quien entraba: el
 *  backend lo verificaba en cada endpoint pero nunca se lo decía, así que el
 *  menú le ofrecía los cinco módulos a todos. */
export interface Yo {
  email: string;
  nombre: string;
  rol: "superadmin" | "admin" | "miembro" | "";
  puede: {
    ver_configuracion: boolean;
    editar_configuracion: boolean;
    modo_live: boolean;
    ver_usuarios: boolean;
    gestionar_admins: boolean;
  };
}

/** Estado de la certificación contra ARCA, **independiente** de si el veredicto
 *  ya lo mira. En modo sombra `visible` es false: el dato existe pero el
 *  semáforo todavía no lo usa. */
export interface EstadoArca {
  /** CONFIRMADO · ESPERANDO_DATA · SIN_CODIGO · SIN_FECHA · … `null` = nunca
   *  pasó por el certifier. */
  estado: string | null;
  confirmado: boolean;
  visible: boolean;
}

export interface Deposito {
  id: string;
  titular: string | null;
  monto: number | null;
  moneda: string | null;
  /** De qué es el voucher: YAPE, PLIN · INTERBANK, BCP, CMAC PIURA... */
  origen: string;
  operacion: string | null;
  creado_en: number | null;
  veredicto: "green" | "yellow" | "red";
  veredicto_label: string;
  /** Por qué no se aprueba (`veredicto.CAUSA_*` del backend). Solo en rojo, y
   *  ausente en los pendings anteriores a que el backend lo mandara. */
  veredicto_causa?: string | null;
  motivos: string[];
  verificados: string;
  diferencias: number;
  comparacion: FilaComparacion[];
  vouchers_url: string[];
  vouchers_count: number;
  modo: string | null;
  env: string | null;
  movimiento: Movimiento | null;
  movimiento_linea: string;
  /** Ausente en lo que devuelve un backend anterior al certificador. Durante
   *  un despliegue conviven las dos versiones, así que se chequea. */
  arca: EstadoArca | null;
}

export interface MotivoRechazo {
  id: string;
  label: string;
}

async function authHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString() ?? "";
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${idToken}`,
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  // La URL se lee por llamada y no al importar el modulo: la config llega de
  // `/config.json` despues de que este modulo se cargo. Congelarla arriba
  // dejaba `undefined` de base y la request salia contra el origen del portal,
  // donde el 404 se ve como un error del API.
  const resp = await fetch(`${config().apiUrl}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers ?? {}) },
  });
  if (!resp.ok) {
    let detalle = `${resp.status} ${resp.statusText}`;
    try {
      const cuerpo = await resp.json();
      if (cuerpo?.error) detalle = cuerpo.error;
    } catch {
      /* la respuesta no era JSON: nos quedamos con el status */
    }
    throw new Error(detalle);
  }
  return resp.json() as Promise<T>;
}

export const api = {
  /** `efectos` pide además la consecuencia concreta de los interruptores —
   *  cuántos depósitos escondería exigir ARCA. Recorre la cola abierta, así
   *  que solo lo pide la pantalla de Configuración. */
  getConfig: (efectos = false) =>
    request<{
      MODE: string; THRESHOLD: number; EXECUTION_MODE: string;
      // Opcionales porque el backend guarda solo las claves que alguien
      // cambió alguna vez: una ausente vale por su default, que en los cuatro
      // casos es el comportamiento que ya estaba corriendo. Compará siempre
      // contra `=== false`, nunca con `!`.
      NOTIFICAR_CHAT?: boolean;
      EXIGIR_ARCA?: boolean;
      APROBACION_LOTE?: boolean;
      PROCESAR_DEPOSITOS?: boolean;
      auditoria?: Record<string, { por: string; ts: number }>;
      efectos?: { exigir_arca_ocultaria: number };
    }>(`/api/config${efectos ? "?efectos=1" : ""}`),
  setConfig: (updates: Record<string, unknown>) =>
    request("/api/config", { method: "POST", body: JSON.stringify(updates) }),

  /** La cola, ya ordenada por el backend: primero lo que se resuelve rápido. */
  getMe: () => request<Yo>("/api/me"),

  /** Aprobar varios de una. Devuelve cuáles salieron y cuáles no: un lote que
   *  informa éxito escondiendo errores es peor que uno que falla entero. */
  aprobarLote: (ids: string[], overrideArca = false) =>
    request<{ aprobados: string[]; fallidos: { id: string; motivo: string }[]; total: number }>(
      "/api/decisions/bulk",
      { method: "POST", body: JSON.stringify({ ids, override_arca: overrideArca }) }),

  getTraza: (executionId: string) =>
    request<{ items: EventoTraza[]; count: number }>(
      `/api/traza/${encodeURIComponent(executionId)}`),

  getLogs: (f: { horas?: number; nivel?: string; q?: string } = {}) => {
    const p = new URLSearchParams();
    if (f.horas) p.set("horas", String(f.horas));
    if (f.nivel) p.set("nivel", f.nivel);
    if (f.q) p.set("q", f.q);
    return request<{ items: EventoTraza[]; count: number }>(
      `/api/logs${p.toString() ? `?${p}` : ""}`);
  },

  /** `rango`: semana (default) · mes · todo · hoy. O `dia` = YYYY-MM-DD.
   *  O un rango a medida: `desde`/`hasta`, ambos YYYY-MM-DD. */
  getPending: (rango?: string, dia?: string, desde?: string, hasta?: string) => {
    const q = new URLSearchParams();
    if (dia) q.set("dia", dia);
    else if (desde && hasta) { q.set("desde", desde); q.set("hasta", hasta); }
    else if (rango) q.set("rango", rango);
    const cola = q.toString() ? `?${q}` : "";
    return request<{ items: Deposito[]; count: number; exigir_arca: boolean }>(
      `/api/pending${cola}`);
  },
  getPendingDetail: (id: string) =>
    request<Deposito & { raw: any }>(`/api/pending/${encodeURIComponent(id)}`),

  /** Vuelve a preguntarle a ARCA por este depósito, ahora. Devuelve el
   *  depósito con el estado fresco; no decide nada. */
  recertificar: (id: string) =>
    request<Deposito>(`/api/pending/${encodeURIComponent(id)}/recertificar`,
                      { method: "POST" }),

  /** Motivos del catálogo real de Admin: el rechazo viaja con su `id`. */
  getRejectReasons: () => request<{ items: MotivoRechazo[] }>("/api/reject-reasons"),

  /** Dispara la misma Step Function que el botón de Google Chat. */
  decidir: (
    id: string,
    decision: "approved" | "rejected",
    opciones: {
      reject_reason?: string;
      code_real?: string;
      /** Aprobar sin que el banco haya confirmado el abono. Queda registrado:
       *  es el dato a mirar si después aparece un abono que nunca llegó. */
      override_arca?: boolean;
    } = {},
  ) =>
    request<{ ok: boolean; next_id: string | null }>(
      `/api/pending/${encodeURIComponent(id)}/decision`,
      { method: "POST", body: JSON.stringify({ decision, ...opciones }) },
    ),

  /** Devuelve un tramo. `siguiente_hasta` es el punto para pedir el
   *  siguiente hacia atrás en el tiempo; `null` = no hay más. */
  getHistorico: (from: number, to: number, filters: Record<string, string> = {}) => {
    const params = new URLSearchParams({ from: String(from), to: String(to), ...filters });
    return request<{
      items: any[]; count: number;
      hay_mas: boolean; siguiente_hasta: number | null;
    }>(`/api/historico?${params}`);
  },

  getMetrics: (from?: number, to?: number) => {
    const params = new URLSearchParams();
    if (from) params.set("from", String(from));
    if (to) params.set("to", String(to));
    return request<any>(`/api/metrics?${params}`);
  },

  getUsers: () =>
    request<{ items: any[]; puede_gestionar: string[] }>("/api/users"),
  upsertUser: (u: { email: string; role: string; name?: string; active?: boolean }) =>
    request("/api/users", { method: "POST", body: JSON.stringify(u) }),
  deleteUser: (email: string) =>
    request(`/api/users/${encodeURIComponent(email)}`, { method: "DELETE" }),
};

/** Formatea un monto como lo muestra el resto del sistema. */
export function fmtMonto(monto: number | null | undefined, moneda?: string | null) {
  if (monto === null || monto === undefined) return "—";
  const simbolo = moneda === "USD" ? "US$" : "S/";
  return `${simbolo} ${Number(monto).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "hace 24 min" — cuánto lleva esperando el cliente. */
export function haceCuanto(epoch: number | null | undefined) {
  if (!epoch) return "—";
  const min = Math.max(0, Math.floor((Date.now() / 1000 - epoch) / 60));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${min % 60} min`;
  return `${Math.floor(h / 24)} d`;
}
